import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Envelope, ArrowRight, CheckCircle } from "@phosphor-icons/react";

/**
 * FarmDiariesOptIn — compact email capture for early-adopter growers.
 * POSTs to `/api/newsletter`. Tagged with `source=farm-diaries` server-side
 * (payload extended below) so we can segment when the feature launches.
 */
export default function FarmDiariesOptIn() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await http.post("/newsletter", { email: email.trim() });
      setDone(true);
      toast.success("You're on the list — we'll email you when Farm Diaries opens.");
    } catch {
      toast.error("Could not sign you up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="farm-diaries-optin"
      className="mt-12 rounded-2xl border border-black/10 bg-gradient-to-br from-[#F5EBDC] to-white p-6 md:p-8"
    >
      <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
        <Envelope size={16} weight="fill" />
        <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase">Early access</span>
      </div>
      <div className="mt-2 font-serif-display text-2xl md:text-3xl leading-tight text-[#151312]">
        Be the first to open a Farm Diary.
      </div>
      <p className="mt-2 text-[15px] text-[#3A3830] max-w-xl">
        Farm Diaries let you record planting, rainfall, pests, harvests and everything else in one place — season after season. We're building it now. Get one email when your diary is ready to open.
      </p>

      {done ? (
        <div
          data-testid="farm-diaries-optin-done"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
        >
          <CheckCircle size={14} weight="fill" /> You're on the list
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="mt-5 flex flex-wrap items-stretch gap-2 max-w-lg"
          data-testid="farm-diaries-optin-form"
        >
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@farm.example"
            data-testid="farm-diaries-optin-email"
            className="flex-1 min-w-[240px] rounded-full border border-black/15 bg-white px-4 py-2 text-sm focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="farm-diaries-optin-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-60"
          >
            {busy ? "Adding…" : (<>Notify me <ArrowRight size={11} weight="bold" /></>)}
          </button>
        </form>
      )}
      <p className="mt-3 text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F]">
        One email at launch. No spam. Unsubscribe any time.
      </p>
    </section>
  );
}
